import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { addBasemap } from "@/lib/basemap";
import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";

import { fleet, type Geofence } from "@/api/client";
import { POLL } from "@/lib/format";

/** Geofencing for one asset, with the boundary drawn.
 *
 *  Dattatray's point 6: *"The geo fence setting has to be visible on asset
 *  live data UI with a map link that can draw on map the actual boundary: It
 *  shal indicate Geo fencing : Status if it is set, If yes by which level and
 *  reason Number."*
 *
 *  READ ONLY, AND THAT IS A GATE DECISION, NOT A SIMPLIFICATION. Creating a
 *  fence lives in Fleet, behind `GATE_FLEET` + `IMMOBILIZE`. `GATE_ASSET_DATA`
 *  is in EVERY plan including basic, so a create button here would put "stop
 *  a vehicle, later, unattended" behind the weaker of the two gates -- the
 *  exact confusion OFL-07a was written to end, where a BASIC account saw the
 *  Asset Control tab and every button returned 403.
 *
 *  THE LEVEL AND THE REASON ARE BOTH SHOWN because they answer different
 *  questions: the level is WHO will be holding the vehicle (and therefore who
 *  can release it), the reason is the 2-bit code the device will report. The
 *  reason renders as a NUMBER -- the per-level vocabulary is undefined, and a
 *  provisional label read off a screen becomes the definition.
 *
 *  SEVERAL FENCES ARE NORMAL: each LEVEL may set one, so a financer's "stay
 *  in Maharashtra" and a fleet manager's "stay in the depot zone" coexist and
 *  each places its own hold. All of them are drawn.
 */

const STATE: Record<string, { label: string; colour: string }> = {
  armed: { label: "Armed", colour: "var(--ok)" },
  warned: { label: "Warned", colour: "var(--warn)" },
  breached: { label: "Breached", colour: "var(--bad)" },
  off: { label: "Off", colour: "var(--t-dim)" },
  //: SUPERSEDED BY A NEWER FENCE at this level, which is different from
  //: switched off: an operator asking why a vehicle is still held needs to
  //: tell them apart. The server stopped listing these, so this is for a
  //: caller that asks for history.
  replaced: { label: "Replaced", colour: "var(--t-dim)" },
};

function FenceMap({ fences, at }: {
  fences: Geofence[];
  /** the vehicle's own position, when it has one */
  at: { lat: number; lng: number } | null;
}) {
  const el = useRef<HTMLDivElement | null>(null);
  const map = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!el.current || map.current) return;
    const m = L.map(el.current, { zoomControl: true, attributionControl: true,
                                  preferCanvas: true });
    addBasemap(m);
    map.current = m;
    return () => { m.remove(); map.current = null; };
  }, []);

  useEffect(() => {
    const m = map.current;
    if (!m) return;
    //: REDRAWN WHOLESALE each time rather than diffed. A fence list is a
    //: handful of circles and a poll is every few seconds; tracking which
    //: layer belongs to which fence would be state to get wrong for no gain.
    m.eachLayer((layer) => { if (!(layer instanceof L.TileLayer)) m.removeLayer(layer); });

    const bounds: L.LatLngExpression[] = [];
    for (const f of fences) {
      const colour = STATE[f.state]?.colour ?? "var(--t-dim)";
      //: A CSS VARIABLE CANNOT GO INTO LEAFLET, which reads the value rather
      //: than resolving it in CSS. Resolved here against the live theme, so
      //: the circles follow a light/dark switch like everything else.
      const hex = getComputedStyle(document.documentElement)
        .getPropertyValue(colour.replace("var(", "").replace(")", "")).trim()
        || "#888";
      L.circle([f.centre_lat, f.centre_lng], {
        radius: f.radius_m, color: hex, weight: 2,
        fillColor: hex, fillOpacity: 0.08,
      }).addTo(m).bindTooltip(
        `${f.name} | ${f.level_name}` +
        (f.reason !== null ? `, reason ${f.reason}` : "") +
        ` | ${STATE[f.state]?.label ?? f.state}`);
      bounds.push([f.centre_lat, f.centre_lng]);
    }
    if (at) {
      L.circleMarker([at.lat, at.lng], {
        radius: 5, color: "#1d7fd8", fillColor: "#1d7fd8", fillOpacity: 1,
      }).addTo(m).bindTooltip("last known position");
      bounds.push([at.lat, at.lng]);
    }
    if (bounds.length === 0) return;
    //: FIT THE FENCES AND THE VEHICLE TOGETHER. Showing the boundary without
    //: the vehicle leaves the one question this card exists for -- is it
    //: inside? -- unanswerable at a glance.
    const fitted = L.latLngBounds(bounds as L.LatLngTuple[]);
    const widest = Math.max(...fences.map((f) => f.radius_m), 50);
    m.fitBounds(fitted.pad(0.2), { maxZoom: 16 });
    if (bounds.length === 1) m.setView(bounds[0], widest > 2000 ? 11 : 14);
    //: Leaflet measures the container on creation; this card can mount while
    //: its tier is still laying out.
    setTimeout(() => m.invalidateSize(), 0);
  }, [fences, at?.lat, at?.lng]);

  return <div ref={el} style={{ height: 220, borderRadius: 8,
                                overflow: "hidden" }} />;
}

export function GeofenceCard({ assetId, position }: {
  assetId: string | null;
  position: { lat: number; lng: number } | null;
}) {
  const q = useQuery({
    queryKey: ["fleet", "geofences", assetId],
    queryFn: () => fleet.geofences(assetId ?? undefined),
    enabled: Boolean(assetId),
    refetchInterval: POLL.DASHBOARD,
  });

  //: NOT RENDERED AT ALL when the caller cannot read fences. The endpoint is
  //: behind GATE_FLEET, which a basic plan does not have, and an error panel
  //: saying "403" on a page that otherwise works is noise rather than
  //: information.
  if (q.isError) return null;

  const fences = q.data?.fences ?? [];

  return (
    <div className="panel panel-pad" style={{ marginBottom: 14 }}>
      <div className="lw-vc-sk" style={{ marginBottom: 8 }}>Geofencing</div>

      {/* STATUS FIRST, IN WORDS. "not set" is the common answer and must be
          unambiguous -- an empty panel reads as "failed to load". */}
      {fences.length === 0 ? (
        <div className="cap">
          {q.isLoading ? "reading…" : "Not set for this asset."}
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gap: 6, marginBottom: 10 }}>
            {fences.map((f) => {
              const st = STATE[f.state] ?? { label: f.state, colour: "var(--t-dim)" };
              return (
                <div key={f.fence_id} style={{ display: "flex", gap: 8,
                     alignItems: "baseline", flexWrap: "wrap", fontSize: 12.5 }}>
                  <b style={{ color: st.colour }}>{st.label}</b>
                  <span style={{ color: "var(--t-bright)" }}>{f.name}</span>
                  <span className="cap">
                    {(f.radius_m / 1000).toFixed(f.radius_m < 1000 ? 2 : 1)} km radius
                  </span>
                  {/* THE CONSEQUENCE, SHOWN WITH THE CONSEQUENCE. The level
                      and the reason describe what a breach WILL do -- who
                      will hold the vehicle and why -- so listing them beside
                      "Armed" made an idle fence read as something already in
                      force. Worse, a reason of 0 is literally "unspecified",
                      so an armed fence displayed a meaningless number with
                      the same weight as its state.

                      They appear once the fence has actually acted, where
                      they answer the question an operator is then asking:
                      who is holding this vehicle, and can I release it. */}
                  {(f.state === "breached" || f.state === "outside") && (
                    <>
                      <span className="cap">{f.level_name}</span>
                      {f.reason !== null && (
                        <span className="cap" title="The 2-bit reason the immobilise carries. What each number MEANS is per level and not yet defined.">
                          reason {f.reason}
                        </span>
                      )}
                    </>
                  )}
                  {/* ARMED SAYS WHAT WOULD HAPPEN, in one phrase rather than
                      a row of attributes: an operator reading an idle fence
                      wants to know it is watching and who would act. */}
                  {f.state === "armed" && (
                    <span className="cap">
                      would stop at {f.level_name}
                    </span>
                  )}
                  {f.scope === "fleet" && (
                    <span className="chip" style={{ fontSize: 10.5 }}
                      title="A fleet-wide fence: it applies to every asset of this company, not just this one.">
                      fleet-wide
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <FenceMap fences={fences} at={position} />
        </>
      )}
    </div>
  );
}
